import React from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Minus, Plus, Trash2, ShoppingBag, ArrowLeft } from "lucide-react";
import { money } from "@/lib/format";

export default function CartPage() {
  const { items, updateQty, removeItem, subtotal } = useCart();
  const nav = useNavigate();

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <ShoppingBag size={56} className="text-gold/40 mx-auto mb-5" />
        <h1 className="font-serif text-3xl text-cream mb-3">Your cart is empty</h1>
        <p className="text-cream/60 mb-6">Add some gourmet goodness to get started.</p>
        <Button className="btn-gold rounded-full px-8 py-6 font-semibold" onClick={() => nav("/shop")} data-testid="cart-page-shop-btn">Shop Now</Button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <h1 className="font-serif text-3xl sm:text-4xl font-bold text-cream mb-8">Your Cart</h1>
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4">
          {items.map((it) => (
            <div key={it.key} className="flex gap-4 gold-border-card rounded-xl bg-card-surface p-4" data-testid={`cart-page-item-${it.key}`}>
              <img src={it.image} alt={it.name} className="h-24 w-24 rounded-lg object-cover border border-gold/20" />
              <div className="flex-1 min-w-0">
                <p className="font-serif text-cream font-semibold">{it.name}</p>
                <p className="text-cream/60 text-sm">{it.size_label}</p>
                <p className="font-mono text-gold mt-1">{money(it.unit_price)}</p>
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center gap-2">
                    <button onClick={() => updateQty(it.key, it.quantity - 1)} className="h-8 w-8 rounded-full border border-gold/40 flex items-center justify-center text-cream hover:bg-gold/10" data-testid={`cart-page-dec-${it.key}`}><Minus size={14} /></button>
                    <span className="font-mono w-6 text-center text-cream">{it.quantity}</span>
                    <button onClick={() => updateQty(it.key, it.quantity + 1)} className="h-8 w-8 rounded-full border border-gold/40 flex items-center justify-center text-cream hover:bg-gold/10" data-testid={`cart-page-inc-${it.key}`}><Plus size={14} /></button>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-mono text-cream">{money(it.unit_price * it.quantity)}</span>
                    <button onClick={() => removeItem(it.key)} className="text-cream/40 hover:text-destructive" data-testid={`cart-page-remove-${it.key}`}><Trash2 size={17} /></button>
                  </div>
                </div>
              </div>
            </div>
          ))}
          <Button variant="ghost" className="text-gold hover:text-gold-bright" onClick={() => nav("/shop")} data-testid="cart-continue-shopping"><ArrowLeft size={16} className="mr-1" /> Continue Shopping</Button>
        </div>

        <div className="lg:col-span-1">
          <div className="gold-border-card rounded-xl bg-card-surface p-6 sticky top-24">
            <h2 className="font-serif text-xl text-gold mb-4">Order Summary</h2>
            <div className="flex justify-between text-cream/80 mb-2"><span>Subtotal</span><span className="font-mono">{money(subtotal)}</span></div>
            <p className="text-cream/40 text-xs mb-4">Delivery & discounts applied at checkout.</p>
            <div className="border-t border-gold/20 pt-4 flex justify-between text-cream font-semibold text-lg">
              <span>Total</span><span className="font-mono text-gold">{money(subtotal)}</span>
            </div>
            <Button className="btn-gold w-full rounded-full py-6 mt-5 font-semibold" onClick={() => nav("/checkout")} data-testid="cart-page-checkout-btn">Proceed to Checkout</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
